import { TweenLite } from 'gsap';
import { ANIMATIONS, CONFIG } from '../config';
import resultTemplate from '../../../templates/partials/organisms/MD139_Liveticker/vue-result-template.hbs';
import BfvImage from '../BfvImage';
import initModules, { initSingleModule } from '../initModules';
import { svgContainsClass, svgAddClass, isTotallyInViewport, isTotallyOutsideViewport } from '../../tools/helpers';

class BfvLiveticker {
  constructor(options = {}) {
    const vue = {
      results: {
        template: resultTemplate,
        el: '#bfv-liveticker__entries',
      },
    };

    this.$root = options.element;
    this.$stageWrapper = this.$root.querySelector('.js-stage-wrapper');
    this.$videoTeaserWrapper = this.$root.querySelector('.js-video-teaser-wrapper');

    this.data = null;
    this.userIsLoggedIn = this.$root.getAttribute('data-logged-in') === 'true';
    this.goalAnimationStarted = {};
    this.substitutionAnimationStarted = {};

    this.Vue = window.divae.Vue;
    this.resultComponentInitalData = {
      wrapperClass: this,
      results: [],
      likes: 0,
      loading: true,
      nextResults: null,
      isLoggedIn: this.userIsLoggedIn,
      notLoggedInMessage: this.$root.getAttribute('data-not-logged-in-message'),
      logInLink: {
        href: this.$root.getAttribute('data-log-in-href'),
        text: this.$root.getAttribute('data-log-in-text'),
        title: this.$root.getAttribute('data-log-in-title'),
      },
    };

    this.apiRoute = this.$root.getAttribute('data-api-route');
    this.stageApiRoute = this.$root.getAttribute('data-stage-api-route');
    this.videoTeaserApiRoute = this.$root.getAttribute('data-video-teaser-api-route');
    this.reloadTime = this.$root.getAttribute('data-reload-time');
    this.lastLoadedStageContent = null;

    this.additionalLivetickerHeaders = {};

    if (this.apiRoute) {
      this.initVueComponents(vue);
    }
  }

  init() {
    window.addEventListener(CONFIG.events.RESIZE, () => {
      this.vueResultComponent.resetHeights();
    });

    if (this.apiRoute) { // set interval and do first load immediately
      this.loadInterval = setInterval(() => {
        this.loadData();
      }, this.reloadTime);
      this.loadData();
    } else {
      console.error('Please specify an API Route');
    }

    if (this.videoTeaserApiRoute) {
      this.loadVideoTeaser();
    }
  }

  /**
   * update the views after new data is loaded
   */
  dataLoaded() {
    this.loadStage();
    this.vueResultComponent.updateData(this.data);
  }

  /**
   * initialize the vue Component (methods, data, template, ...)
   *
   * @param vueOptions
   */
  initVueComponents(vueOptions) {
    this.vueResultComponent = new this.Vue({
      el: vueOptions.results.el,
      template: vueOptions.results.template,
      data: Object.assign({}, this.resultComponentInitalData),
      props: [],
      methods: {
        loadMore() {
          this.wrapperClass.loadMore();
        },
        updateData(newData) {
          Object.assign(this.$data, this.wrapperClass.resultComponentInitalData, newData);
        },
        resetHeights(resultArray = this.$refs.result) {
          this.wrapperClass.resetHeights(resultArray);
        },
      },
      updated() {
        this.$nextTick(() => {
          const resultArray = this.$refs.result;
          this.wrapperClass.resetHeights(resultArray);
          this.wrapperClass.unobfuscateFont(resultArray);
          this.wrapperClass.loadImages(resultArray);
          this.wrapperClass.setAnimationFunctionality(resultArray);
          this.wrapperClass.setLikeFunctionality(resultArray);
          this.wrapperClass.setOptionsFunctionality(resultArray);
        });
      },
    });
  }

  /**
   * event happening on Button Click
   */
  loadMore() {
    this.loadData(this.data.nextResults);
  }

  /**
   * connect to Api and load the Data
   */
  loadData() {
    this.data = Object.assign({}, this.data, { loading: true });

    window.divae.restApi
      .loadData(this.apiRoute, undefined, this.additionalLivetickerHeaders)
      .then((_response) => {
        if (_response.status === 304) { // no changes
          return;
        }

        const response = _response;
        if (response.ifModifiedSinceTimestamp) {
          this.apiRoute = `${this.apiRoute.split('/ifmodifiedsince')[0]}/ifmodifiedsince/${response.ifModifiedSinceTimestamp}`;
          this.additionalLivetickerHeaders = {
            'If-Modified-Since': response.ifModifiedSinceTimestamp,
          };
        } else {
          this.apiRoute = this.apiRoute.split('/ifmodifiedsince')[0];
          this.additionalLivetickerHeaders = {};
        }

        this.data = response;
        this.data.likes = this.data.results.reduce((likes, result) => likes + (result.likes || 0), 0);
        this.addYoutubeControlParameterToVideoUrls();
        this.data.loading = false;

        if (!response.loadNew) {
          clearInterval(this.loadInterval);
        }

        this.dataLoaded();
      })
      .catch(() => {
        this.data.loading = false;
      });
  }

  addYoutubeControlParameterToVideoUrls() {
    if (this.data.results) {
      this.data.results = this.data.results.map((result) => {
        if (result.video && result.video.videoUrl) {
          const modifiedResult = { ...result };
          modifiedResult.video.videoUrl = result.video.videoUrl.includes('?') ? `${result.video.videoUrl}&enablejsapi=1` : `${result.video.videoUrl}?enablejsapi=1`;
          return modifiedResult;
        }
        return result;
      });
    }
  }

  /**
   * lazyload of stage module on top of the ticker
   */
  loadStage() {
    window.divae.restApi
      .loadData(this.stageApiRoute, 'application/xhtml+xml', {}, true)
      .then((_response) => {
        // we don't want image reloads if nothing changed
        // but can't compare to actual content, which gets modified by initModules()
        // so we compare to last loaded version
        if (_response.status === 304) { // no changes
          return;
        }

        if (this.lastLoadedStageContent !== _response) {
          this.$stageWrapper.innerHTML = _response;
          this.lastLoadedStageContent = _response;
          initModules({ rootElement: this.$stageWrapper });
        }
      });
  }

  /**
   * lazyload of video teaser module on top of the ticker
   */
  loadVideoTeaser() {
    window.divae.restApi
      .loadData(this.videoTeaserApiRoute, 'application/xhtml+xml')
      .then((_response) => {
        if (_response.status === 304) { // no changes
          return;
        }

        this.$videoTeaserWrapper.innerHTML = _response;
        initModules({ rootElement: this.$videoTeaserWrapper });
      });
  }

  // noinspection JSMethodCanBeStatic
  /**
   * make like button work
   *
   * @property {Object[]} this.data.results
   * @property {string} this.data.results[].likeApiRoute
   * @param resultArray
   */
  setLikeFunctionality(resultArray = []) {
    resultArray.forEach(($result, index, $results) => {
      const likedClass = 'bfv-liveticker-entry__likes--liked';
      const $likeButton = $result.querySelector('.js-liveticker-likes svg');
      const $likes = $result.querySelector('.js-liveticker-likes');
      const $likeCount = $result.querySelector('.js-liveticker-like-count');

      if (!$likeButton) {
        return; // sections don't have options
      }

      const likeApiRoute = this.data.results[index].likeApiRoute;
      const unlikeApiRoute = this.data.results[index].unlikeApiRoute;

      if (!svgContainsClass($likeButton, 'js-like-active')) { // init only once
        const $totalLikes = this.$root.querySelector('.js-total-likes');

        svgAddClass($likeButton, 'js-like-active');
        $likeButton.addEventListener('click', (event) => {
          BfvLiveticker.animateLikeClick(event.currentTarget);
          const route = $likes.classList.contains(likedClass) ? unlikeApiRoute : likeApiRoute;

          if (this.userIsLoggedIn) { // only logged in users can like
            window.divae.restApi
              .loadData(route, 'application/json', {}, true)
              .then((_response) => {
                if (_response && _response.status === 200) { // if liked / disliked successfully, update like count and total like count
                  if ($likes.classList.contains(likedClass)) {
                    $likeCount.innerHTML = parseInt($likeCount.innerHTML, 10) - 1;
                    $totalLikes.innerHTML = parseInt($totalLikes.innerHTML, 10) - 1;
                  } else {
                    $likeCount.innerHTML = parseInt($likeCount.innerHTML, 10) + 1;
                    $totalLikes.innerHTML = parseInt($totalLikes.innerHTML, 10) + 1;
                  }

                  $likes.classList.toggle(likedClass);
                }
              });
          } else { // if not logged in show options
            this.showOptions($results, $result, true);
          }
        });
      }
    });
  }

  /**
   * make goals and substitutions animated on scrolling
   *
   * @param resultArray
   */
  setAnimationFunctionality(resultArray = []) {
    window.addEventListener(CONFIG.events.VIEWPORT_SCROLL, () => {
      resultArray
        .filter($result => $result.querySelector('.js-liveticker-goal') || $result.querySelector('.js-liveticker-substitution'))
        .forEach(($result, index) => {
          const $goal = $result.querySelector('.js-liveticker-goal');
          const $substitution = $result.querySelector('.js-liveticker-substitution');

          // goals
          if ($goal && isTotallyInViewport($goal) && !this.goalAnimationStarted[index]) { // only animate once we can see it
            this.goalAnimationStarted[index] = true;
            BfvLiveticker.animateGoal($result, index);
          } else if ($goal && isTotallyOutsideViewport($goal) && this.goalAnimationStarted.hasOwnProperty(index)) { // reset animation
            this.resetGoalAnimation($goal, index);
          }

          // substitutions
          if ($substitution && isTotallyInViewport($substitution) && !this.substitutionAnimationStarted[index]) { // only animate once we can see it
            this.substitutionAnimationStarted[index] = true;
            BfvLiveticker.animateSubstitution($result, index);
          } else if ($substitution && isTotallyOutsideViewport($substitution) && this.substitutionAnimationStarted[index]) { // eslint-disable-line max-len, reset animation
            this.resetSubstitutionAnimation($substitution, index);
          }
        });
    });
  }

  // <editor-fold defaultstate="collapsed" desc="*** Animation Methods ***">
  /**
   * Animation for clicking Like / Unlike
   *
   * @param $likeButton
   */
  static animateLikeClick($likeButton) {
    TweenLite.to($likeButton, ANIMATIONS.bounce.DURATION, { // bounce up
      top: -6,
    });

    TweenLite.to($likeButton, ANIMATIONS.bounce.OUT_DURATION, { // bounce down
      top: 0,
      delay: ANIMATIONS.bounce.DELAY,
      ease: ANIMATIONS.bounce.CURVE,
    });
  }

  /**
   * Animation for goals
   *
   * @param $card
   */
  static animateGoal($card) {
    const $football = $card.querySelector('.js-liveticker-goal .js-football') || // use existing if possible
      $card.querySelector('.js-liveticker-time .icon').cloneNode(true); // otherwise create
    $football.classList.add('js-football');
    const $circle = $football.querySelector('circle');
    const $goalContent = $card.querySelector('.js-liveticker-goal-content');

    // initial football state
    TweenLite.set($football, {
      bottom: '0%',
      display: 'block',
      left: '30%',
      opacity: 1,
      position: 'absolute',
      rotation: 0,
      scale: 0,
      transformOrigin: '50% 50%',
      width: 24,
      y: -24,
    });

    // hide data initially
    TweenLite.set($goalContent, {
      opacity: 0,
    });

    // football coloring
    if ($circle) { // own goal
      $circle.style.fill = 'transparent';
    } else {
      $football.querySelector('svg').style.fill = 'white';
    }

    $card.querySelector('.js-liveticker-goal').appendChild($football);

    // football final state
    TweenLite.to($football, 1.5, {
      bottom: '80%',
      left: '75%',
      rotation: 1080,
      scale: 5,
      transformOrigin: '50% 50%',
      y: 0,
      onComplete: () => {
        $football.style.display = 'none';
      },
    });

    // fade in data
    TweenLite.to($goalContent, ANIMATIONS.default.DOUBLE_DURATION, {
      opacity: 1,
      delay: 1.5 - ANIMATIONS.default.DOUBLE_DURATION,
    });

    // fade out ball
    TweenLite.to($football, ANIMATIONS.default.DOUBLE_DURATION, {
      opacity: 0,
      delay: 1.5 - ANIMATIONS.default.DOUBLE_DURATION,
    });
  }

  /**
   * Reset animation for substitutions
   *
   * @param $goal
   * @param index
   */
  resetGoalAnimation($goal, index) {
    const $content = $goal.querySelector('.js-liveticker-goal-content');
    const $football = $goal.querySelector('.js-football');
    TweenLite.killTweensOf($football);
    TweenLite.killTweensOf($content);
    $content.removeAttribute('style');
    $football.style.display = 'none';
    this.goalAnimationStarted[index] = false; // reset animation
  }

  /**
   * Animation for substitutions
   *
   * @param $card
   */
  static animateSubstitution($card) {
    const $substitutionArrows = $card.querySelector('.js-liveticker-substitution-icon');
    const $substitutionTeam = $substitutionArrows.querySelector('.js-team-logo');
    const $player1 = $card.querySelector('.bfv-liveticker-substitution__player--1');
    const $player2 = $card.querySelector('.bfv-liveticker-substitution__player--2');

    // initial football state
    TweenLite.set($substitutionArrows, {
      rotation: -160,
    });

    TweenLite.set($substitutionTeam, {
      rotation: 160,
    });

    TweenLite.set($player1, {
      top: 224,
    });

    TweenLite.set($player2, {
      top: -104,
    });

    TweenLite.to($substitutionArrows, 1, {
      rotation: 0,
      ease: ANIMATIONS.default.CURVE,
    });

    TweenLite.to($substitutionTeam, 1, {
      rotation: 0,
      ease: ANIMATIONS.default.CURVE,
    });

    TweenLite.to($player1, 1, {
      top: 88,
      ease: ANIMATIONS.default.CURVE,
    });

    TweenLite.to($player2, 1, {
      top: 88,
    });
  }

  /**
   * Reset animation for substitutions
   *
   * @param $substitution
   * @param index
   */
  resetSubstitutionAnimation($substitution, index) {
    const $arrows = $substitution.querySelector('.js-liveticker-substitution-icon');
    const $teamLogo = $substitution.querySelector('.js-team-logo');
    const $player1 = $substitution.querySelector('.bfv-liveticker-substitution__player--1');
    const $player2 = $substitution.querySelector('.bfv-liveticker-substitution__player--2');

    TweenLite.killTweensOf($arrows);
    TweenLite.killTweensOf($teamLogo);
    TweenLite.killTweensOf($player1);
    TweenLite.killTweensOf($player2);
    $arrows.removeAttribute('style');
    $teamLogo.removeAttribute('style');
    $player1.removeAttribute('style');
    $player2.removeAttribute('style');
    this.substitutionAnimationStarted[index] = false;
  }

  /**
   * Animation for showing the event options
   *
   * @param $result
   * @param notLoggedIn
   */
  static animateOptions($result, notLoggedIn = false) {
    const $card = $result.querySelector('.js-liveticker-card');
    let $optionsCard = $result.querySelector('.js-liveticker-options-card');

    if (notLoggedIn) { // show correct option overlay
      $optionsCard.style.display = 'none';
      $optionsCard = $optionsCard.nextElementSibling;
    } else {
      $optionsCard.style.display = 'flex';
    }

    const $timeAndIconBox = $result.querySelector('.js-liveticker-time');
    const $nextCard = $result.nextElementSibling ? (
      $result.nextElementSibling.querySelector('.js-liveticker-card') || // card
      $result.nextElementSibling // section
    ) : null;
    const $connector = $result.querySelector('.js-liveticker-connector');
    let $closeButtonWrapper = $optionsCard.nextElementSibling;
    let $closeButton = $closeButtonWrapper.querySelector('button');

    if (!$closeButton) { // not logged in has other DOM structure
      $closeButtonWrapper = $closeButtonWrapper.nextElementSibling;
      $closeButton = $closeButtonWrapper.querySelector('button');
    }

    const cardHeight = $result.getBoundingClientRect().height;
    const nextCardMarginTop = $nextCard ?
      Math.round(parseFloat(($nextCard.currentStyle || window.getComputedStyle($nextCard)).marginTop)) :
      0;
    const minOptionHeight = 136;

    if (cardHeight < minOptionHeight) {
      TweenLite.set($result, {
        background: 'transparent',
      });

      TweenLite.set($card, {
        background: 'transparent',
      });

      TweenLite.set($optionsCard, {
        height: 0,
        minHeight: 0,
        top: (cardHeight - minOptionHeight) / 2,
        y: 0,
        yPercent: 0,
      });
    }

    TweenLite.set($optionsCard, {
      height: 0,
      minHeight: 0,
    });

    TweenLite.set($card, {
      marginBottom: nextCardMarginTop,
    });

    TweenLite.set($connector, {
      marginTop: -nextCardMarginTop + 8, // 8: initial value
    });

    TweenLite.set($closeButton, {
      top: -34,
    });

    TweenLite.to($closeButton, ANIMATIONS.default.DURATION, {
      top: 20,
    });

    TweenLite.to($card, ANIMATIONS.default.DURATION, {
      height: cardHeight,
      marginBottom: nextCardMarginTop,
    });

    TweenLite.to($optionsCard, ANIMATIONS.default.DURATION, {
      height: cardHeight,
      minHeight: minOptionHeight,
    });

    // hide time icon area if it would overlap the social sharing buttons
    if ($timeAndIconBox &&
      ($timeAndIconBox.getBoundingClientRect().bottom - ($timeAndIconBox.getBoundingClientRect().height / 2) >
        Math.min($card.getBoundingClientRect().top, $optionsCard.getBoundingClientRect().top))) {
      $timeAndIconBox.style.display = 'none';
    }
  }

  /**
   * Animation for closing the event options
   *
   * @param $result
   */
  static animateCloseOptions($result) {
    const $optionsCard = $result.querySelector('.js-liveticker-options-card');
    const $notLoggedInOptionsCard = $result.querySelector('.js-liveticker-options-card + .js-liveticker-options-card');

    let $closeButtonWrapper = $optionsCard.nextElementSibling;
    let $closeButton = $closeButtonWrapper.querySelector('button');

    if (!$closeButton) { // not logged in has other DOM structure
      $closeButtonWrapper = $closeButtonWrapper.nextElementSibling;
      $closeButton = $closeButtonWrapper.querySelector('button');
    }

    TweenLite.to($closeButton, ANIMATIONS.default.DURATION, {
      top: -34,
    });

    TweenLite.to($optionsCard, ANIMATIONS.default.DURATION, {
      height: 0,
      minHeight: 0,
    });

    if ($notLoggedInOptionsCard) {
      TweenLite.to($notLoggedInOptionsCard, ANIMATIONS.default.DURATION, {
        height: 0,
        minHeight: 0,
      });
    }
  }
  // </editor-fold>


  // noinspection JSMethodCanBeStatic
  /**
   * make options open and close buttons work
   *
   * @param resultArray
   */
  setOptionsFunctionality(resultArray = []) { // <editor-fold defaultstate="collapsed">
    resultArray.forEach(($result, index, $results) => {
      const $optionsButton = $result.querySelector('.js-liveticker-options');
      const $closeOptionsButton = $result.querySelector('.js-liveticker-close-options');

      if (!$optionsButton || !$closeOptionsButton) {
        return; // sections don't have options
      }

      $optionsButton.addEventListener('click', () => {
        this.showOptions($results, $result);
      });

      $closeOptionsButton.addEventListener('click', () => {
        this.hideOptions($result);
      });
    });
  } // </editor-fold>

  /**
   * display options instead of event
   *
   * @param $results
   * @param $result
   * @param notLoggedIn
   */
  showOptions($results, $result, notLoggedIn = false) {
    this.resetHeights($results); // close all open options
    initModules({ rootElement: $result }); // init social share buttons

    BfvLiveticker.animateOptions($result, notLoggedIn);
  }

  // noinspection JSMethodCanBeStatic
  /**
   * hide the options again
   *
   * @param $result
   */
  hideOptions($result) {
    BfvLiveticker.animateCloseOptions($result);

    const $timeAndIconBox = $result.querySelector('.js-liveticker-time');

    if ($timeAndIconBox && $timeAndIconBox.style.display === 'none') {
      $timeAndIconBox.style.display = 'inline-flex';
    }
  }

  // noinspection JSMethodCanBeStatic
  /**
   * Resets heights and closes options if open
   *
   * @param resultArray
   */
  resetHeights(resultArray = []) {
    resultArray.forEach(($result) => {
      const $card = $result.querySelector('.js-liveticker-card');
      const $optionsCard = $result.querySelector('.js-liveticker-options-card');
      $result.style.removeProperty('height');

      if ($card) {
        $card.style.removeProperty('height');
      }

      if ($optionsCard && $optionsCard.getBoundingClientRect().height) {
        const cardHeight = $card.getBoundingClientRect().height;
        const minOptionHeight = 136;
        $optionsCard.style.height = `${cardHeight}px`;

        if (cardHeight < minOptionHeight) {
          $optionsCard.style.top = `${(cardHeight - minOptionHeight) / 2}px`;
        } else {
          $optionsCard.style.top = 0;
        }
      }

      const $timeAndIconBox = $result.querySelector('.js-liveticker-time');
      if ($timeAndIconBox && $timeAndIconBox.style.display === 'none') { // show time icon area again
        $timeAndIconBox.style.display = 'inline-flex';
      }
    });
  }

  /**
   * images don't get loaded automatically so we must do it manually
   *
   * @param resultArray
   */
  loadImages(resultArray = []) {
    resultArray.forEach(($result) => {
      const $images = $result.querySelectorAll('.bfv-image__loader[data-module="BfvImage"]');

      Array.prototype.slice.call($images).forEach(($image) => {
        if (!$image.classList.contains('bfv-image--load-finished')) {
          const newImage = new BfvImage({ element: $image });
          newImage.disableAutoLoad = true;
          newImage.init();
        }

        const event = document.createEvent('event');
        event.initEvent(CONFIG.events.BFV_IMAGE_LOAD, false, true);
        $image.dispatchEvent(event);
      });
    });
  }

  /**
   * font for goals doesn't get rendered readable from the start, so decode it
   * @param resultArray
   */
  unobfuscateFont(resultArray = []) {
    resultArray.forEach(($result) => {
      const $obfuscation = $result.querySelector('*[data-module="BfvFontObfuscation"]');

      if ($obfuscation) {
        const $obfuscationNodes = $obfuscation.querySelectorAll('[data-class-name]');
        $obfuscationNodes.forEach(($obfuscationNode) => {
          $obfuscationNode.classList.add($obfuscationNode.getAttribute('data-class-name'));
        });

        initSingleModule({ element: $obfuscation, name: $obfuscation.getAttribute(['data-module']) });
      }
    });
  }
}

export default BfvLiveticker;



// WEBPACK FOOTER //
// ./src/javascript/main/BfvLiveticker/index.js